import { AuthUser } from '@/types/auth';

const AUTH_SNAPSHOT_KEY = 'auth_user_snapshot';
const AUTH_SNAPSHOT_TIMESTAMP_KEY = 'auth_user_snapshot_timestamp';
const SNAPSHOT_TTL = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Cache management for complete auth state
 * Prevents privilege loss during network failures
 */
export const authStateCache = {
  /**
   * Store complete auth snapshot in sessionStorage
   */
  saveSnapshot(user: AuthUser): void {
    try {
      sessionStorage.setItem(AUTH_SNAPSHOT_KEY, JSON.stringify(user));
      sessionStorage.setItem(AUTH_SNAPSHOT_TIMESTAMP_KEY, Date.now().toString());
      console.log('[AuthStateCache] Snapshot saved:', user.email, 'roles:', user.roles);
    } catch (error) {
      console.error('[AuthStateCache] Failed to save snapshot:', error);
    }
  },

  /**
   * Retrieve last known good auth snapshot if still valid
   */
  getSnapshot(): AuthUser | null {
    try {
      const snapshot = sessionStorage.getItem(AUTH_SNAPSHOT_KEY);
      const timestamp = sessionStorage.getItem(AUTH_SNAPSHOT_TIMESTAMP_KEY);

      if (!snapshot || !timestamp) {
        return null;
      }

      const age = Date.now() - parseInt(timestamp, 10);
      if (age > SNAPSHOT_TTL) {
        console.log('[AuthStateCache] Snapshot expired, clearing');
        this.clearSnapshot();
        return null;
      }

      const user = JSON.parse(snapshot) as AuthUser;
      console.log('[AuthStateCache] Retrieved snapshot:', user.email, 'age:', Math.round(age / 1000), 'seconds');
      return user;
    } catch (error) {
      console.error('[AuthStateCache] Failed to retrieve snapshot:', error);
      return null;
    }
  },

  /**
   * Clear stored snapshot
   */
  clearSnapshot(): void {
    try {
      sessionStorage.removeItem(AUTH_SNAPSHOT_KEY);
      sessionStorage.removeItem(AUTH_SNAPSHOT_TIMESTAMP_KEY);
      console.log('[AuthStateCache] Snapshot cleared');
    } catch (error) {
      console.error('[AuthStateCache] Failed to clear snapshot:', error);
    }
  },

  /**
   * Check if we have a valid snapshot available
   */
  hasValidSnapshot(): boolean {
    const timestamp = sessionStorage.getItem(AUTH_SNAPSHOT_TIMESTAMP_KEY);
    if (!timestamp) return false;
    
    const age = Date.now() - parseInt(timestamp, 10);
    return age < SNAPSHOT_TTL;
  }
};
